import React, { useState } from 'react';
import { ChevronLeft, CheckCircle2, AlertCircle, Lock, Info, Bug } from 'lucide-react';
import { Button } from '../components/Button';
type SupportState = 'recommended' | 'allowed' | 'allowed_with_warning' | 'not_recommended';
interface Structure {
  name: string;
  description: string;
  supportState: SupportState;
  helperText: string;
  warningText?: string;
}
interface StructureRecommendationProps {
  onNext: () => void;
  onBack: () => void;
}
export function StructureRecommendation({
  onNext,
  onBack
}: StructureRecommendationProps) {
  const [isSelectingAlternate, setIsSelectingAlternate] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState('Full Body');
  const [showWarning, setShowWarning] = useState(false);
  const [showNotRecommendedInfo, setShowNotRecommendedInfo] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  // Mock structures with support states
  const [structures, setStructures] = useState<Structure[]>([{
    name: 'Full Body',
    description: 'Hit all major muscle groups in every session. Best for 2-3 days a week.',
    supportState: 'recommended',
    helperText: 'Best match for your schedule, recovery, and training goal.'
  }, {
    name: 'Upper / Lower',
    description: 'Alternate between upper body and lower body days. Great for 4 days a week.',
    supportState: 'allowed',
    helperText: 'This option can work for your current setup.'
  }, {
    name: 'Body-Part Split',
    description: 'Focus on 1-2 muscle groups per session. Ideal for 5+ days a week.',
    supportState: 'not_recommended',
    helperText: 'This structure is not available for your current setup in MVP.'
  }]);
  const handleStructureSelect = (struct: Structure) => {
    if (struct.supportState === 'not_recommended') {
      setShowNotRecommendedInfo(struct.name);
      return;
    }
    setSelectedStructure(struct.name);
    if (struct.supportState === 'allowed_with_warning') {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };
  const getRecommendedStructure = () => {
    return structures.find(s => s.supportState === 'recommended') || structures[0];
  };
  const getSelectedStructureData = () => {
    return structures.find(s => s.name === selectedStructure) || structures[0];
  };
  const getCTAText = () => {
    const selected = getSelectedStructureData();
    if (selected.supportState === 'recommended' && !isSelectingAlternate) {
      return 'Use recommended structure';
    }
    return 'Confirm selection';
  };
  const loadDebugState = (state: string) => {
    setShowDebug(false);
    switch (state) {
      case 'recommended-view':
        setIsSelectingAlternate(false);
        setSelectedStructure('Full Body');
        setStructures([{
          name: 'Full Body',
          description: 'Hit all major muscle groups in every session. Best for 2-3 days a week.',
          supportState: 'recommended',
          helperText: 'Best match for your schedule, recovery, and training goal.'
        }, {
          name: 'Upper / Lower',
          description: 'Alternate between upper body and lower body days. Great for 4 days a week.',
          supportState: 'allowed',
          helperText: 'This option can work for your current setup.'
        }, {
          name: 'Body-Part Split',
          description: 'Focus on 1-2 muscle groups per session. Ideal for 5+ days a week.',
          supportState: 'not_recommended',
          helperText: 'This structure is not available for your current setup in MVP.'
        }]);
        break;
      case 'selection-mixed':
        setIsSelectingAlternate(true);
        setSelectedStructure('Full Body');
        setShowWarning(false);
        setStructures([{
          name: 'Full Body',
          description: 'Hit all major muscle groups in every session. Best for 2-3 days a week.',
          supportState: 'recommended',
          helperText: 'Best match for your schedule, recovery, and training goal.'
        }, {
          name: 'Upper / Lower',
          description: 'Alternate between upper body and lower body days. Great for 4 days a week.',
          supportState: 'allowed',
          helperText: 'This option can work for your current setup.'
        }, {
          name: 'Body-Part Split',
          description: 'Focus on 1-2 muscle groups per session. Ideal for 5+ days a week.',
          supportState: 'allowed_with_warning',
          helperText: 'This structure can still be used, but it may be harder to recover from or less balanced for your current schedule.',
          warningText: "You can still choose this structure. We'll generate a more conservative version to keep it manageable."
        }]);
        break;
      case 'warning-selected':
        setIsSelectingAlternate(true);
        setSelectedStructure('Body-Part Split');
        setShowWarning(true);
        setStructures([{
          name: 'Full Body',
          description: 'Hit all major muscle groups in every session. Best for 2-3 days a week.',
          supportState: 'recommended',
          helperText: 'Best match for your schedule, recovery, and training goal.'
        }, {
          name: 'Upper / Lower',
          description: 'Alternate between upper body and lower body days. Great for 4 days a week.',
          supportState: 'allowed',
          helperText: 'This option can work for your current setup.'
        }, {
          name: 'Body-Part Split',
          description: 'Focus on 1-2 muscle groups per session. Ideal for 5+ days a week.',
          supportState: 'allowed_with_warning',
          helperText: 'This structure can still be used, but it may be harder to recover from or less balanced for your current schedule.',
          warningText: "You can still choose this structure. We'll generate a more conservative version to keep it manageable."
        }]);
        break;
      case 'not-recommended-visible':
        setIsSelectingAlternate(true);
        setSelectedStructure('Full Body');
        setShowWarning(false);
        setStructures([{
          name: 'Full Body',
          description: 'Hit all major muscle groups in every session. Best for 2-3 days a week.',
          supportState: 'recommended',
          helperText: 'Best match for your schedule, recovery, and training goal.'
        }, {
          name: 'Upper / Lower',
          description: 'Alternate between upper body and lower body days. Great for 4 days a week.',
          supportState: 'allowed',
          helperText: 'This option can work for your current setup.'
        }, {
          name: 'Body-Part Split',
          description: 'Focus on 1-2 muscle groups per session. Ideal for 5+ days a week.',
          supportState: 'not_recommended',
          helperText: 'This structure is not available for your current setup in MVP.'
        }]);
        break;
    }
  };
  const recommendedStruct = getRecommendedStructure();
  const selectedStruct = getSelectedStructureData();
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-1085">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-1086">
        <Bug size={14} data-id="element-1087" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[500px] overflow-y-auto" data-id="element-1088">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-1089">
            Debug Panel
          </p>
          <button onClick={() => loadDebugState('recommended-view')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1090">
            ▶ Recommendation view
          </button>
          <button onClick={() => loadDebugState('selection-mixed')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1091">
            ▶ Selection — mixed states
          </button>
          <button onClick={() => loadDebugState('warning-selected')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1092">
            ▶ Warning option selected
          </button>
          <button onClick={() => loadDebugState('not-recommended-visible')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-1093">
            ▶ Not recommended visible
          </button>
        </div>}

      {/* Not Recommended Info Modal */}
      {showNotRecommendedInfo && <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-1094">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 duration-200" data-id="element-1095">
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-1096">
              <Lock size={24} data-id="element-1097" />
            </div>
            <h3 className="text-xl font-extrabold text-slate-800 mb-3 text-center" data-id="element-1098">
              Not available
            </h3>
            <p className="text-slate-600 font-medium text-sm leading-relaxed mb-4 text-center" data-id="element-1099">
              This structure is not recommended for your current setup in MVP.
            </p>
            <div className="bg-slate-50 rounded-2xl p-4 mb-4" data-id="element-1100">
              <p className="text-xs font-bold text-slate-700 mb-2" data-id="element-1101">
                Why this is not the main recommendation:
              </p>
              <ul className="text-xs text-slate-600 space-y-1 ml-4 list-disc" data-id="element-1102">
                <li data-id="element-1103">Lower training frequency match</li>
                <li data-id="element-1104">Reduced recovery fit for your current level</li>
              </ul>
            </div>
            <Button variant="primary" fullWidth onClick={() => setShowNotRecommendedInfo(null)} data-id="element-1105">
              Got it
            </Button>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-1106">
        <button onClick={isSelectingAlternate ? () => setIsSelectingAlternate(false) : onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-1107">
          <ChevronLeft size={28} data-id="element-1108" />
        </button>
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-1109">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-1110" />
        </div>
        <div className="w-10" data-id="element-1111" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-32" data-id="element-1112">
        {!isSelectingAlternate ? <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1113">
            <div className="mb-8 mt-2" data-id="element-1114">
              <h1 className="text-3xl font-extrabold text-slate-800 mb-4 leading-tight" data-id="element-1115">
                We have a plan for you!
              </h1>
              <p className="text-slate-500 font-medium leading-relaxed" data-id="element-1116">
                Based on your training frequency, background, and goal, we
                recommend:
              </p>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-sm border-2 border-emerald-400 relative overflow-hidden mb-4" data-id="element-1117">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -z-10" data-id="element-1118" />

              <div className="flex items-start justify-between mb-4" data-id="element-1119">
                <div className="flex items-center gap-3" data-id="element-1120">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0" data-id="element-1121">
                    <CheckCircle2 size={24} data-id="element-1122" />
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-800" data-id="element-1123">
                    {recommendedStruct.name}
                  </h2>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-extrabold rounded-full" data-id="element-1124">
                  Recommended
                </span>
              </div>

              <p className="text-slate-600 font-medium leading-relaxed mb-3" data-id="element-1125">
                {recommendedStruct.description}
              </p>

              <div className="flex items-start gap-2 bg-emerald-50 rounded-2xl p-3" data-id="element-1126">
                <Info size={16} className="text-emerald-600 shrink-0 mt-0.5" data-id="element-1127" />
                <p className="text-xs font-medium text-emerald-700 leading-relaxed" data-id="element-1128">
                  {recommendedStruct.helperText}
                </p>
              </div>
            </div>
          </div> : <div className="animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-1129">
            <div className="mb-8 mt-2" data-id="element-1130">
              <h1 className="text-3xl font-extrabold text-slate-800 mb-2 leading-tight" data-id="element-1131">
                Select your preferred structure
              </h1>
              <p className="text-slate-500 font-medium leading-relaxed" data-id="element-1132">
                Choose the training split that best fits your lifestyle and
                preferences.
              </p>
            </div>

            <div className="space-y-4" data-id="element-1133">
              {structures.map(struct => {
            const isSelected = selectedStructure === struct.name;
            const isRecommended = struct.supportState === 'recommended';
            const isAllowed = struct.supportState === 'allowed';
            const isWarning = struct.supportState === 'allowed_with_warning';
            const isNotRecommended = struct.supportState === 'not_recommended';
            return <div key={struct.name} data-id="element-1134">
                    <button onClick={() => handleStructureSelect(struct)} disabled={isNotRecommended} className={`
                        w-full text-left p-5 rounded-3xl transition-all duration-200 border-2
                        ${isNotRecommended ? 'opacity-60 cursor-not-allowed bg-slate-50 border-slate-200' : ''}
                        ${!isNotRecommended && isSelected && isRecommended ? 'bg-emerald-50/50 border-emerald-500 shadow-sm' : ''}
                        ${!isNotRecommended && isSelected && !isRecommended ? 'bg-blue-50/50 border-blue-400 shadow-sm' : ''}
                        ${!isNotRecommended && !isSelected ? 'bg-white border-slate-100 hover:border-slate-300 hover:bg-slate-50' : ''}
                        ${isWarning && !isSelected ? 'border-amber-200' : ''}
                      `} data-id="element-1135">
                      <div className="flex items-start justify-between mb-3" data-id="element-1136">
                        <div className="flex items-center gap-2" data-id="element-1137">
                          {isNotRecommended && <Lock className="text-slate-400" size={18} data-id="element-1138" />}
                          <h3 className={`text-lg font-extrabold ${isNotRecommended ? 'text-slate-500' : isSelected && isRecommended ? 'text-emerald-700' : isSelected ? 'text-blue-700' : 'text-slate-800'}`} data-id="element-1139">
                            {struct.name}
                          </h3>
                        </div>

                        <div className="flex items-center gap-2" data-id="element-1140">
                          {isRecommended && <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-extrabold rounded-full" data-id="element-1141">
                              Recommended
                            </span>}
                          {isWarning && <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-[10px] font-extrabold rounded-full flex items-center gap-1" data-id="element-1142">
                              <AlertCircle size={10} data-id="element-1143" />
                              Caution
                            </span>}
                          {isNotRecommended && <span className="px-2.5 py-1 bg-slate-200 text-slate-600 text-[10px] font-extrabold rounded-full" data-id="element-1144">
                              Not recommended
                            </span>}
                          {isSelected && !isNotRecommended && <CheckCircle2 className={isRecommended ? 'text-emerald-500' : 'text-blue-500'} size={20} data-id="element-1145" />}
                        </div>
                      </div>

                      <p className={`text-sm font-medium mb-3 ${isNotRecommended ? 'text-slate-400' : isSelected && isRecommended ? 'text-emerald-600/80' : isSelected ? 'text-blue-600/80' : 'text-slate-500'}`} data-id="element-1146">
                        {struct.description}
                      </p>

                      {/* Caution note — only when warning card is selected */}
                      {isWarning && isSelected && showWarning && <div className="flex items-center gap-1.5 mb-3 animate-in fade-in duration-200" data-id="element-1147">
                          <AlertCircle size={12} className="text-amber-600 shrink-0" data-id="element-1148" />
                          <p className="text-xs font-semibold text-amber-700" data-id="element-1149">
                            May be harder to recover from with your current
                            schedule.
                          </p>
                        </div>}

                      <div className={`flex items-start gap-2 rounded-2xl p-3 ${isNotRecommended ? 'bg-slate-100' : isRecommended ? 'bg-emerald-50' : isSelected ? 'bg-blue-50' : isWarning ? 'bg-amber-50' : 'bg-blue-50'}`} data-id="element-1150">
                        <Info size={14} className={`shrink-0 mt-0.5 ${isNotRecommended ? 'text-slate-500' : isRecommended ? 'text-emerald-600' : isSelected ? 'text-blue-600' : isWarning ? 'text-amber-600' : 'text-blue-600'}`} data-id="element-1151" />
                        <p className={`text-xs font-medium leading-relaxed ${isNotRecommended ? 'text-slate-600' : isRecommended ? 'text-emerald-700' : isSelected ? 'text-blue-700' : isWarning ? 'text-amber-700' : 'text-blue-700'}`} data-id="element-1152">
                          {struct.helperText}
                        </p>
                      </div>
                    </button>

                    {/* Warning helper block — shown below card when warning option is selected */}
                    {isSelected && isWarning && showWarning && struct.warningText && <div className="mt-3 mx-1 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-1153">
                          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-4" data-id="element-1154">
                            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" data-id="element-1155" />
                            <p className="text-xs font-medium text-amber-800 leading-relaxed" data-id="element-1156">
                              {struct.warningText}
                            </p>
                          </div>
                        </div>}
                  </div>;
          })}
            </div>
          </div>}
      </div>

      {/* Sticky Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-50 via-stone-50 to-transparent pt-12 z-20 pointer-events-none" data-id="element-1157">
        <div className="pointer-events-auto space-y-3" data-id="element-1158">
          {!isSelectingAlternate ? <>
              <Button variant="primary" fullWidth onClick={onNext} data-id="element-1159">
                Use recommended structure
              </Button>
              <button onClick={() => setIsSelectingAlternate(true)} className="w-full text-center text-sm font-bold text-slate-600 hover:text-slate-800 py-2" data-id="element-1160">
                Choose a different structure
              </button>
            </> : <Button variant="primary" fullWidth onClick={onNext} data-id="element-1161">
              {getCTAText()}
            </Button>}
        </div>
      </div>
    </div>;
}